import { supabase } from './supabase';
import { Category, Exam, Attempt, ExamResult, Bookmark, Question, LeaderboardEntry, AdminStats, QuestionReport, User } from '../types';
import { CATEGORIES, MOCK_EXAMS } from '../constants';

// Keys for LocalStorage Fallback
const LS_CATEGORIES_KEY = 'thrusted_categories';
const LS_EXAMS_KEY = 'thrusted_exams';
const LS_ATTEMPTS_KEY = 'thrusted_attempts';
const LS_BOOKMARKS_KEY = 'thrusted_bookmarks';
const LS_REPORTS_KEY = 'thrusted_reports';

// Keys for tracking deleted items (to prevent them from reappearing via merge)
const LS_DELETED_CATEGORIES_KEY = 'thrusted_deleted_categories';
const LS_DELETED_EXAMS_KEY = 'thrusted_deleted_exams';

const getProfileCacheKey = (userId: string) => `thrusted_profile_${userId}`;

// Helper functions for deleted items tracking
const getDeletedCategoryIds = (): Set<string> => {
  const data = localStorage.getItem(LS_DELETED_CATEGORIES_KEY);
  return data ? new Set(JSON.parse(data)) : new Set();
};

const getDeletedExamIds = (): Set<string> => {
  const data = localStorage.getItem(LS_DELETED_EXAMS_KEY);
  return data ? new Set(JSON.parse(data)) : new Set();
};

const markCategoryAsDeleted = (id: string) => {
  const deleted = getDeletedCategoryIds();
  deleted.add(id);
  localStorage.setItem(LS_DELETED_CATEGORIES_KEY, JSON.stringify([...deleted]));
};

const unmarkCategoryAsDeleted = (id: string) => {
  const deleted = getDeletedCategoryIds();
  deleted.delete(id);
  localStorage.setItem(LS_DELETED_CATEGORIES_KEY, JSON.stringify([...deleted]));
};

const markExamAsDeleted = (id: string) => {
  const deleted = getDeletedExamIds();
  deleted.add(id);
  localStorage.setItem(LS_DELETED_EXAMS_KEY, JSON.stringify([...deleted]));
};

const unmarkExamAsDeleted = (id: string) => {
  const deleted = getDeletedExamIds();
  deleted.delete(id);
  localStorage.setItem(LS_DELETED_EXAMS_KEY, JSON.stringify([...deleted]));
};

/**
 * Data Service
 * 
 * Strategy:
 * 1. Try Supabase (if configured)
 * 2. Fallback to LocalStorage (persistence without backend)
 * 3. Fallback to Constants (initial seed data)
 * 4. Exclude deleted items from all sources
 */

export const fetchCategories = async (): Promise<Category[]> => {
  let supabaseCategories: Category[] = [];
  let localCategories: Category[] = [];
  const deletedIds = getDeletedCategoryIds();

  // 1. Try Supabase
  if (supabase) {
    // Sync local deletions to Supabase if any exist
    if (deletedIds.size > 0) {
      const deletePromises = Array.from(deletedIds).map(async (deletedId) => {
        const { data, error } = await supabase.from('categories').delete().eq('id', deletedId).select();
        if (!error && data && data.length > 0) {
          unmarkCategoryAsDeleted(deletedId);
          deletedIds.delete(deletedId);
        }
      });
      await Promise.all(deletePromises);
    }

    const { data, error } = await supabase.from('categories').select('*');
    if (!error && data) {
      supabaseCategories = data as Category[];
    }
  }

  // 2. Get LocalStorage data
  const lsData = localStorage.getItem(LS_CATEGORIES_KEY);
  if (lsData) {
    localCategories = JSON.parse(lsData);
  }

  // 3. Merge: Supabase + LocalStorage (dedupe by ID, prefer local data)
  const mergedMap = new Map<string, Category>();

  // Add Supabase categories first (excluding deleted)
  supabaseCategories
    .filter(c => !deletedIds.has(c.id))
    .forEach(c => mergedMap.set(c.id, c));

  // Override/add with local categories (excluding deleted)
  localCategories
    .filter(c => !deletedIds.has(c.id))
    .forEach(c => mergedMap.set(c.id, c));

  const merged = Array.from(mergedMap.values());

  if (merged.length > 0) {
    return merged;
  }

  // 4. Fallback to Mock (excluding deleted)
  return CATEGORIES.filter(c => !deletedIds.has(c.id));
};

export const fetchExams = async (): Promise<Exam[]> => {
  let supabaseExams: Exam[] = [];
  let localExams: Exam[] = [];
  const deletedIds = getDeletedExamIds();

  // 1. Try Supabase
  if (supabase) {
    // Sync local deletions to Supabase if any exist
    if (deletedIds.size > 0) {
      const deletePromises = Array.from(deletedIds).map(async (deletedId) => {
        const { data, error } = await supabase.from('exams').delete().eq('id', deletedId).select();
        if (!error && data && data.length > 0) {
          unmarkExamAsDeleted(deletedId);
          deletedIds.delete(deletedId);
        }
      });
      await Promise.all(deletePromises);
    }

    const { data, error } = await supabase.from('exams').select('*');
    if (!error && data) {
      supabaseExams = data.map((e: any) => ({
        id: e.id,
        title: e.title,
        category: e.category,
        subcategory: e.subcategory,
        questions: e.questions,
        // Map DB columns (which might be mixed case due to quotes in SQL or standard snake_case)
        durationMinutes: e.durationMinutes ?? e.duration_minutes,
        totalQuestions: e.totalQuestions ?? e.total_questions,
        positiveMarks: e.positiveMarks ?? e.positive_marks,
        negativeMarks: e.negativeMarks ?? e.negative_marks,
        isPremium: e.is_premium,
        // Fix: Map time fields for Live Exams
        startTime: e.start_time,
        endTime: e.end_time
      })) as Exam[];
    }
  }

  // 2. Get LocalStorage data
  const lsData = localStorage.getItem(LS_EXAMS_KEY);
  if (lsData) {
    localExams = JSON.parse(lsData);
  }

  // 3. Merge: Supabase + LocalStorage (dedupe by ID, prefer newer local data)
  const mergedMap = new Map<string, Exam>();

  // Add Supabase exams first (excluding deleted)
  supabaseExams
    .filter(e => !deletedIds.has(e.id))
    .forEach(e => mergedMap.set(e.id, e));

  // Override/add with local exams (local takes precedence for updates, excluding deleted)
  localExams
    .filter(e => !deletedIds.has(e.id))
    .forEach(e => mergedMap.set(e.id, e));

  const merged = Array.from(mergedMap.values());

  // If we have merged data, return it
  if (merged.length > 0) {
    return merged;
  }

  // 4. Fallback to Mock (excluding deleted)
  return MOCK_EXAMS.filter(e => !deletedIds.has(e.id));
};

export const saveCategory = async (category: Category): Promise<Category> => {
  let saved = category;

  // 1. Supabase
  if (supabase) {
    const { error } = await supabase.from('categories').upsert(category);
    if (error) console.error('Supabase save failed', error);
  }

  // 2. LocalStorage (Always sync local for redundancy in demo)
  const current = await fetchCategories();
  const exists = current.find(c => c.id === category.id);
  const updated = exists
    ? current.map(c => c.id === category.id ? category : c)
    : [...current, category];

  localStorage.setItem(LS_CATEGORIES_KEY, JSON.stringify(updated));

  return saved;
};

export const deleteCategory = async (categoryId: string): Promise<boolean> => {
  // 1. Mark as deleted (to prevent reappearing from Supabase/Constants)
  markCategoryAsDeleted(categoryId);

  // 2. Delete from Supabase
  if (supabase) {
    const { data, error } = await supabase.from('categories').delete().eq('id', categoryId).select();
    if (error || !data || data.length === 0) {
      console.error('Supabase delete failed', error || 'No rows affected (RLS block or missing row)');
      alert(`Database Warning: FAILED to delete category "${categoryId}" from Supabase. You may lack database admin privileges (role='admin' in public.profiles table) or the category is referenced by active exams.`);
    }
  }

  // 3. Remove from localStorage
  const lsData = localStorage.getItem(LS_CATEGORIES_KEY);
  if (lsData) {
    const current = JSON.parse(lsData) as Category[];
    const updated = current.filter(c => c.id !== categoryId);
    localStorage.setItem(LS_CATEGORIES_KEY, JSON.stringify(updated));
  }

  return true;
};

export const saveExam = async (exam: Exam): Promise<Exam> => {
  let saved = exam;

  // Prepare object for Supabase (only columns that exist in schema)
  const dbExam = {
    id: exam.id,
    title: exam.title,
    category: exam.category,
    subcategory: exam.subcategory,
    questions: exam.questions,
    durationMinutes: exam.durationMinutes,
    totalQuestions: exam.totalQuestions,
    positiveMarks: exam.positiveMarks,
    negativeMarks: exam.negativeMarks,
    is_premium: exam.isPremium || false,
    start_time: exam.startTime ? new Date(exam.startTime).toISOString() : null,
    end_time: exam.endTime ? new Date(exam.endTime).toISOString() : null
  };

  // 1. Supabase
  if (supabase) {
    console.log('Saving exam to Supabase:', dbExam.id, dbExam.title);
    const { data, error } = await supabase.from('exams').upsert(dbExam).select();
    if (error) {
      console.error('❌ Supabase exam save failed:', error.message, error.details, error.hint);
      console.error('Exam data:', JSON.stringify(dbExam, null, 2));
      // Alert admin so they know the save failed
      alert(`Failed to save exam to database: ${error.message}. Exam saved locally only.`);
    } else {
      console.log('✅ Exam saved to Supabase:', data);
    }
  }

  // 2. LocalStorage
  const current = await fetchExams();
  const exists = current.find(e => e.id === exam.id);
  const updated = exists
    ? current.map(e => e.id === exam.id ? exam : e)
    : [...current, exam];

  localStorage.setItem(LS_EXAMS_KEY, JSON.stringify(updated));

  return saved;
};

export const deleteExam = async (examId: string): Promise<boolean> => {
  // 1. Mark as deleted (to prevent reappearing from Supabase/Constants)
  markExamAsDeleted(examId);

  // 2. Delete from Supabase
  if (supabase) {
    const { data, error } = await supabase.from('exams').delete().eq('id', examId).select();
    if (error || !data || data.length === 0) {
      console.error('Supabase delete failed', error || 'No rows affected (RLS block or missing row)');
      alert(`Database Warning: FAILED to delete exam "${examId}" from Supabase. You may lack database admin privileges (role='admin' in public.profiles table).`);
    }
  }

  // 3. Delete from LocalStorage
  const lsData = localStorage.getItem(LS_EXAMS_KEY);
  if (lsData) {
    const current = JSON.parse(lsData) as Exam[];
    const updated = current.filter(e => e.id !== examId);
    localStorage.setItem(LS_EXAMS_KEY, JSON.stringify(updated));
  }

  return true;
};

// Sync all exams from localStorage to Supabase (Admin only)
export const syncAllExamsToSupabase = async (): Promise<{ success: number, failed: number, errors: string[] }> => {
  const result = { success: 0, failed: 0, errors: [] as string[] };

  if (!supabase) {
    result.errors.push('Supabase not configured');
    return result;
  }

  const lsData = localStorage.getItem(LS_EXAMS_KEY);
  if (!lsData) {
    result.errors.push('No local exams to sync');
    return result;
  }

  const localExams = JSON.parse(lsData) as Exam[];
  console.log(`Syncing ${localExams.length} exams to Supabase...`);

  for (const exam of localExams) {
    // Only include columns that exist in Supabase schema (user confirmed adding these)
    const dbExam = {
      id: exam.id,
      title: exam.title,
      category: exam.category,
      subcategory: exam.subcategory,
      questions: exam.questions,
      durationMinutes: exam.durationMinutes,
      totalQuestions: exam.totalQuestions,
      positiveMarks: exam.positiveMarks,
      negativeMarks: exam.negativeMarks,
      is_premium: exam.isPremium || false,
      start_time: exam.startTime ? new Date(exam.startTime).toISOString() : null,
      end_time: exam.endTime ? new Date(exam.endTime).toISOString() : null
    };

    const { error } = await supabase.from('exams').upsert(dbExam);
    if (error) {
      result.failed++;
      result.errors.push(`${exam.title}: ${error.message}`);
      console.error(`❌ Failed to sync ${exam.title}:`, error);
    } else {
      result.success++;
      console.log(`✅ Synced ${exam.title}`);
    }
  }

  console.log(`Sync complete: ${result.success} success, ${result.failed} failed`);
  return result;
};

export const saveAttempt = async (userId: string, result: ExamResult): Promise<void> => {
  const attemptData = {
    user_id: userId,
    exam_id: result.examId,
    score: result.score,
    total_questions: result.totalQuestions,
    correct_count: result.correctCount,
    wrong_count: result.wrongCount,
    time_taken: result.timeTaken,
    created_at: new Date().toISOString()
  };

  // 1. Supabase
  if (supabase) {
    const { error } = await supabase.from('attempts').insert(attemptData);
    if (error) {
      if (error.code === '23503') {
        console.warn(`Exam ID ${result.examId} not found in DB. Saving attempt locally only.`);
      } else {
        console.error('Failed to save attempt to Supabase', error);
      }
      saveAttemptLocal(userId, result);
    }
  } else {
    // 2. LocalStorage
    saveAttemptLocal(userId, result);
  }
};

const saveAttemptLocal = (userId: string, result: ExamResult) => {
  const attempts = JSON.parse(localStorage.getItem(LS_ATTEMPTS_KEY) || '[]');
  const newAttempt = {
    id: `local-${Date.now()}`,
    ...result,
    userId,
    createdAt: new Date().toISOString()
  };
  localStorage.setItem(LS_ATTEMPTS_KEY, JSON.stringify([...attempts, newAttempt]));
};

export const fetchUserAttempts = async (userId: string): Promise<Attempt[]> => {
  // 1. Supabase
  if (supabase) {
    const { data, error } = await supabase
      .from('attempts')
      .select('*, exams(title)')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (!error && data) {
      return data.map((a: any) => ({
        id: a.id,
        examId: a.exam_id,
        examTitle: a.exams?.title || a.exam_id,
        score: a.score,
        totalQuestions: a.total_questions,
        correctCount: a.correct_count,
        wrongCount: a.wrong_count,
        timeTaken: a.time_taken,
        createdAt: a.created_at
      }));
    }
  }

  // 2. LocalStorage Fallback
  const localAttempts = JSON.parse(localStorage.getItem(LS_ATTEMPTS_KEY) || '[]');
  const userLocalAttempts = localAttempts.filter((a: any) => a.userId === userId);
  const exams = await fetchExams();

  return userLocalAttempts.map((a: any) => ({
    id: a.id,
    examId: a.examId,
    examTitle: exams.find(e => e.id === a.examId)?.title || a.examId,
    score: a.score,
    totalQuestions: a.totalQuestions,
    correctCount: a.correctCount,
    wrongCount: a.wrongCount,
    timeTaken: a.timeTaken,
    createdAt: a.createdAt
  }));
};

export const upgradeUserSubscription = async (userId: string, tier: 'pro' | 'premium'): Promise<boolean> => {
  if (supabase) {
    const { error } = await supabase
      .from('profiles')
      .update({
        subscription_tier: tier,
        subscription_expiry: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString()
      })
      .eq('id', userId);

    if (!error) return true;
  }
  return true;
};

// Retrieve cached profile data
export const getLocalUserProfile = (userId: string) => {
  try {
    const data = localStorage.getItem(getProfileCacheKey(userId));
    return data ? JSON.parse(data) : null;
  } catch (e) {
    return null;
  }
};

// Update local cache only (doesn't write to Supabase) - includes role for offline fallback
export const updateLocalProfileCache = (userId: string, updates: { name?: string, phone?: string, targetExam?: string, email?: string, allowedCategories?: string[], role?: 'admin' | 'user' }) => {
  try {
    const cacheKey = getProfileCacheKey(userId);
    const currentCache = getLocalUserProfile(userId) || {};
    const newCache = { ...currentCache, ...updates };
    localStorage.setItem(cacheKey, JSON.stringify(newCache));
  } catch (e) {
    console.error('Failed to update local profile cache:', e);
  }
};

export const updateUserProfile = async (userId: string, updates: { name?: string, phone?: string, targetExam?: string, email?: string, allowedCategories?: string[] }): Promise<boolean> => {
  // 1. Update Local Cache (Immediate persistence)
  const cacheKey = getProfileCacheKey(userId);
  const currentCache = getLocalUserProfile(userId) || {};
  const newCache = { ...currentCache, ...updates };
  localStorage.setItem(cacheKey, JSON.stringify(newCache));

  // 2. Prepare data for Supabase
  const dbData: any = { id: userId };
  if (updates.name !== undefined) dbData.name = updates.name;
  if (updates.phone !== undefined) dbData.phone = updates.phone;
  if (updates.targetExam !== undefined) dbData.target_exam = updates.targetExam;
  if (updates.email !== undefined) dbData.email = updates.email;
  if (updates.allowedCategories !== undefined) dbData.allowed_categories = updates.allowedCategories;

  if (supabase) {
    try {
      // First try upsert
      const { error: upsertError } = await supabase
        .from('profiles')
        .upsert(dbData, {
          onConflict: 'id',
          ignoreDuplicates: false
        });

      if (upsertError) {
        console.error("Profile upsert failed:", upsertError.message, upsertError.code);

        // If upsert fails (maybe RLS issue), try explicit update
        const { error: updateError } = await supabase
          .from('profiles')
          .update({
            name: updates.name,
            phone: updates.phone,
            target_exam: updates.targetExam,
            email: updates.email,
            allowed_categories: updates.allowedCategories
          })
          .eq('id', userId);

        if (updateError) {
          console.error("Profile update also failed:", updateError.message);

          // Last resort: try insert if row doesn't exist
          const { error: insertError } = await supabase
            .from('profiles')
            .insert(dbData);

          if (insertError) {
            console.error("Profile insert also failed:", insertError.message);
            // At this point, profile was saved to local cache at least
          } else {
            console.log("Profile inserted successfully");
          }
        } else {
          console.log("Profile updated successfully");
        }
      } else {
        console.log("Profile upserted successfully");
      }
    } catch (err) {
      console.error("Profile save exception:", err);
    }
  }
  return true;
};

// Fetch all profiles for administrative tab
export const fetchAllProfiles = async (): Promise<User[]> => {
  if (supabase) {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });
      if (!error && data) {
        return data.map((p: any) => ({
          id: p.id,
          email: p.email,
          role: p.role,
          name: p.name,
          phone: p.phone,
          targetExam: p.target_exam,
          subscriptionTier: p.subscription_tier || 'free',
          allowedCategories: p.allowed_categories || []
        }));
      }
    } catch (e) {
      console.error("Error fetching profiles from Supabase:", e);
    }
  }
  
  // Local storage / Mock fallback: Let's read from local storage profiles or mock users
  const mockUsersList: User[] = [
    { id: 'user-1', email: 'aspirant1@thrusted.in', role: 'user', name: 'Aspirant One', phone: '9876543210', targetExam: 'UPSC CSE', subscriptionTier: 'free', allowedCategories: ['upsc'] },
    { id: 'user-2', email: 'aspirant2@thrusted.in', role: 'user', name: 'Aspirant Two', phone: '9876543211', targetExam: 'APPSC', subscriptionTier: 'pro', allowedCategories: ['appsc'] },
    { id: 'user-3', email: 'aspirant3@thrusted.in', role: 'user', name: 'Aspirant Three', phone: '9876543212', targetExam: 'Banking', subscriptionTier: 'premium', allowedCategories: ['banking'] },
  ];
  
  const storedUsers = localStorage.getItem('thrusted_admin_mock_users');
  if (storedUsers) {
    return JSON.parse(storedUsers);
  }
  
  localStorage.setItem('thrusted_admin_mock_users', JSON.stringify(mockUsersList));
  return mockUsersList;
};

// Update user category permissions as administrator
export const adminUpdateUserCategories = async (userId: string, allowedCategories: string[]): Promise<boolean> => {
  console.log('[AdminUpdate] Updating categories for user:', userId, 'Categories:', allowedCategories);
  
  if (supabase) {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .update({ allowed_categories: allowedCategories })
        .eq('id', userId)
        .select();
      
      console.log('[AdminUpdate] Supabase update result:', { data, error: error?.message, code: error?.code });
      
      if (!error) {
        console.log('[AdminUpdate] Successfully updated in Supabase');
        // Also update local cache
        const cacheKey = getProfileCacheKey(userId);
        const currentCache = getLocalUserProfile(userId);
        if (currentCache) {
          currentCache.allowedCategories = allowedCategories;
          localStorage.setItem(cacheKey, JSON.stringify(currentCache));
        }
        return true;
      }
      console.error("[AdminUpdate] Failed to update user categories in Supabase:", error.message, error.code, error.details);
      alert(`Supabase update failed: ${error.message}\n\nYou may need to run the admin RLS policy migration in Supabase SQL Editor.`);
      return false;
    } catch (e) {
      console.error("[AdminUpdate] Error updating user categories:", e);
      return false;
    }
  }
  
  // LocalStorage / Mock fallback: update inside 'thrusted_admin_mock_users'
  const stored = localStorage.getItem('thrusted_admin_mock_users');
  if (stored) {
    const users = JSON.parse(stored) as User[];
    const updated = users.map(u => u.id === userId ? { ...u, allowedCategories } : u);
    localStorage.setItem('thrusted_admin_mock_users', JSON.stringify(updated));
  }
  
  // Also update the cached profile for that user if it's the current user
  const cacheKey = getProfileCacheKey(userId);
  const currentCache = getLocalUserProfile(userId);
  if (currentCache) {
    currentCache.allowedCategories = allowedCategories;
    localStorage.setItem(cacheKey, JSON.stringify(currentCache));
  }
  
  return true;
};

// Get profile by email (for linking Google OAuth with Email/Password accounts)
export const getProfileByEmail = async (email: string): Promise<{
  id: string;
  role: 'admin' | 'user';
  name?: string;
  phone?: string;
  targetExam?: string;
  subscriptionTier: 'free' | 'pro' | 'premium';
  allowedCategories?: string[];
} | null> => {
  if (supabase) {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, role, name, phone, target_exam, subscription_tier, email, allowed_categories')
        .eq('email', email)
        .single();

      if (data && !error) {
        return {
          id: data.id,
          role: data.role || 'user',
          name: data.name,
          phone: data.phone,
          targetExam: data.target_exam,
          subscriptionTier: data.subscription_tier || 'free',
          allowedCategories: data.allowed_categories || []
        };
      }
    } catch (err) {
      // No profile found for this email, that's okay
    }
  }
  return null;
};

// Check if email already exists in profiles (for duplicate detection)
export const checkEmailExists = async (email: string): Promise<boolean> => {
  if (supabase) {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id')
        .eq('email', email)
        .single();

      return !error && !!data;
    } catch (err) {
      return false;
    }
  }
  return false;
};

// --- Leaderboard & Analytics Services ---
export const fetchExamLeaderboard = async (examId: string): Promise<LeaderboardEntry[]> => {
  if (supabase) {
    try {
      const { data, error } = await supabase
        .from('attempts')
        .select('user_id, score, time_taken, profiles(name)')
        .eq('exam_id', examId)
        .order('score', { ascending: false })
        .order('time_taken', { ascending: true })
        .limit(10);

      if (!error && data) {
        return data.map((d: any, index: number) => ({
          rank: index + 1,
          userId: d.user_id,
          name: d.profiles?.name || 'Anonymous',
          score: d.score,
          timeTaken: d.time_taken
        }));
      }
    } catch (e) {
      console.warn("Leaderboard fetch failed", e);
    }
  }

  return [
    { rank: 1, userId: 'mock-1', name: 'Topper Singh', score: 18, timeTaken: 340 },
    { rank: 2, userId: 'mock-2', name: 'Riya Sharma', score: 17.5, timeTaken: 400 },
    { rank: 3, userId: 'mock-3', name: 'Amit Kumar', score: 16, timeTaken: 450 },
    { rank: 4, userId: 'mock-4', name: 'Sneha P.', score: 14, timeTaken: 380 },
    { rank: 5, userId: 'mock-5', name: 'Rahul V.', score: 12, timeTaken: 500 },
  ];
};

export const calculateRank = async (examId: string, myScore: number): Promise<{ rank: number, percentile: number, total: number }> => {
  if (supabase) {
    try {
      const { count, error } = await supabase
        .from('attempts')
        .select('*', { count: 'exact', head: true })
        .eq('exam_id', examId);

      const { count: betterCount, error: error2 } = await supabase
        .from('attempts')
        .select('*', { count: 'exact', head: true })
        .eq('exam_id', examId)
        .gt('score', myScore);

      if (!error && !error2 && count !== null && betterCount !== null) {
        const total = count || 1;
        const rank = betterCount + 1;
        const percentile = ((total - rank) / total) * 100;
        return { rank, percentile, total };
      }
    } catch (e) {
      console.warn("Rank calc failed", e);
    }
  }

  return { rank: 15, percentile: 85.5, total: 100 };
};

// --- Bookmark Services ---
export const toggleBookmark = async (userId: string, question: Question, examId: string): Promise<boolean> => {
  if (supabase) {
    const { data } = await supabase
      .from('bookmarks')
      .select('id')
      .eq('user_id', userId)
      .eq('question_id', question.id)
      .single();

    if (data) {
      await supabase.from('bookmarks').delete().eq('id', data.id);
      return false;
    } else {
      await supabase.from('bookmarks').insert({
        user_id: userId,
        question_id: question.id,
        question_data: question,
        exam_id: examId
      });
      return true;
    }
  }

  const bookmarks = JSON.parse(localStorage.getItem(LS_BOOKMARKS_KEY) || '[]');
  const existingIndex = bookmarks.findIndex((b: any) => b.userId === userId && b.questionId === question.id);

  if (existingIndex > -1) {
    bookmarks.splice(existingIndex, 1);
    localStorage.setItem(LS_BOOKMARKS_KEY, JSON.stringify(bookmarks));
    return false;
  } else {
    bookmarks.push({
      id: `local-bm-${Date.now()}`,
      userId,
      questionId: question.id,
      questionData: question,
      examId,
      createdAt: new Date().toISOString()
    });
    localStorage.setItem(LS_BOOKMARKS_KEY, JSON.stringify(bookmarks));
    return true;
  }
};

export const fetchBookmarks = async (userId: string): Promise<Bookmark[]> => {
  if (supabase) {
    const { data, error } = await supabase
      .from('bookmarks')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (!error && data) {
      return data.map((b: any) => ({
        id: b.id,
        questionId: b.question_id,
        questionData: b.question_data,
        examId: b.exam_id,
        createdAt: b.createdAt
      }));
    }
  }

  const bookmarks = JSON.parse(localStorage.getItem(LS_BOOKMARKS_KEY) || '[]');
  return bookmarks
    .filter((b: any) => b.userId === userId)
    .map((b: any) => ({
      id: b.id,
      questionId: b.questionId,
      questionData: b.questionData,
      examId: b.exam_id,
      createdAt: b.createdAt
    }));
};

// --- Reporting Services ---
export const submitReport = async (userId: string, report: Omit<QuestionReport, 'id' | 'createdAt' | 'status' | 'userId'>): Promise<boolean> => {
  if (supabase) {
    const { error } = await supabase.from('reports').insert({
      user_id: userId,
      question_id: report.questionId,
      exam_id: report.examId,
      issue_type: report.issueType,
      description: report.description,
      status: 'open'
    });
    if (error) {
      console.error("Failed to submit report", error);
      return false;
    }
    return true;
  }

  const reports = JSON.parse(localStorage.getItem(LS_REPORTS_KEY) || '[]');
  const newReport = {
    id: `rpt-${Date.now()}`,
    userId,
    ...report,
    status: 'open',
    createdAt: new Date().toISOString()
  };
  localStorage.setItem(LS_REPORTS_KEY, JSON.stringify([...reports, newReport]));
  return true;
};

export const fetchReports = async (): Promise<QuestionReport[]> => {
  if (supabase) {
    const { data, error } = await supabase
      .from('reports')
      .select('*')
      .order('created_at', { ascending: false });

    if (!error && data) {
      return data.map((r: any) => ({
        id: r.id,
        userId: r.user_id,
        questionId: r.question_id,
        examId: r.exam_id,
        issueType: r.issue_type,
        description: r.description,
        status: r.status,
        createdAt: r.created_at
      }));
    }
  }
  return JSON.parse(localStorage.getItem(LS_REPORTS_KEY) || '[]');
};

export const updateReportStatus = async (reportId: string, status: 'resolved' | 'dismissed'): Promise<void> => {
  if (supabase) {
    await supabase.from('reports').update({ status }).eq('id', reportId);
    return;
  }
  const reports = JSON.parse(localStorage.getItem(LS_REPORTS_KEY) || '[]');
  const updated = reports.map((r: any) => r.id === reportId ? { ...r, status } : r);
  localStorage.setItem(LS_REPORTS_KEY, JSON.stringify(updated));
};

// --- Question Bank Management ---
export const updateQuestionInExam = async (examId: string, updatedQuestion: Question): Promise<Exam | null> => {
  const exams = await fetchExams();
  const exam = exams.find(e => e.id === examId);
  if (!exam) return null;

  const updatedQuestions = exam.questions.map(q =>
    q.id === updatedQuestion.id ? updatedQuestion : q
  );

  const updatedExam = { ...exam, questions: updatedQuestions };
  return await saveExam(updatedExam);
};

export const deleteQuestionFromExam = async (examId: string, questionId: string): Promise<Exam | null> => {
  const exams = await fetchExams();
  const exam = exams.find(e => e.id === examId);
  if (!exam) return null;

  const updatedQuestions = exam.questions.filter(q => q.id !== questionId);

  const updatedExam = {
    ...exam,
    questions: updatedQuestions,
    totalQuestions: updatedQuestions.length
  };

  return await saveExam(updatedExam);
};

// --- AI Generator ---
export const generateCustomExam = async (
  config: { subjects: string[]; difficulty: string[]; count: number }
): Promise<Exam> => {
  const allExams = await fetchExams();
  let pool: Question[] = [];

  allExams.forEach(exam => {
    const questions = exam.questions.map(q => ({
      ...q,
      subject: q.subject || exam.category
    }));
    pool.push(...questions);
  });

  let filtered = pool.filter(q => {
    if (config.difficulty.length > 0 && !config.difficulty.includes(q.difficulty)) {
      return false;
    }
    if (config.subjects.length > 0) {
      const qSubject = (q.subject || '').toLowerCase();
      const matches = config.subjects.some(s => qSubject.includes(s.toLowerCase()));
      if (!matches) return false;
    }
    return true;
  });

  filtered = filtered.filter((q, index, self) =>
    index === self.findIndex((t) => (t.id === q.id || t.text === q.text))
  );

  filtered.sort(() => Math.random() - 0.5);
  const selected = filtered.slice(0, config.count);

  return {
    id: `custom-${Date.now()}`,
    title: `Custom Test ${new Date().toLocaleDateString()}`,
    category: 'custom',
    durationMinutes: Math.ceil(selected.length * 1.5),
    totalQuestions: selected.length,
    questions: selected,
    positiveMarks: 2,
    negativeMarks: 0.66,
    isPremium: true
  };
};

export const fetchAdminStats = async (): Promise<AdminStats> => {
  const allExams = await fetchExams();
  const realTotalExams = allExams.length;
  const realTotalQuestions = allExams.reduce((sum, exam) => sum + (exam.questions?.length || 0), 0);

  let totalUsers = 0;
  let totalAttempts = 0;
  let usersByTier = { free: 0, pro: 0, premium: 0 };

  if (supabase) {
    try {
      const { count: attemptsCount } = await supabase
        .from('attempts')
        .select('*', { count: 'exact', head: true });
      totalAttempts = attemptsCount || 0;

      const { data: profiles } = await supabase
        .from('profiles')
        .select('subscription_tier');

      if (profiles) {
        totalUsers = profiles.length;
        profiles.forEach((p: any) => {
          const tier = (p.subscription_tier || 'free').toLowerCase();
          if (tier === 'pro') usersByTier.pro++;
          else if (tier === 'premium') usersByTier.premium++;
          else usersByTier.free++;
        });
      }
    } catch (e) {
      console.error("Error fetching detailed stats from DB", e);
    }
  } else {
    totalUsers = 24;
    totalAttempts = 156;
    usersByTier = { free: 20, pro: 3, premium: 1 };
  }

  return {
    totalUsers,
    totalExams: realTotalExams,
    totalQuestions: realTotalQuestions,
    totalAttempts,
    usersByTier
  };
};